// ============================================
// Creator Editor - Form Module (ES6)
// 폼 초기화, 로드, 상태관리
// ============================================

import { showToast, showGlobalLoading, hideGlobalLoading } from '../../utils.js';
import { getCurrentUser } from '../../app/state.js';
import {
  getCurrentEditorData, setEditorDataStatus,
  getInitialEditorState, setInitialEditorState,
  getIntroBlocks, setIntroBlocks,
  setCurrentIntroType,
  getExampleDialogues, setExampleDialogues,
  getSituationalImages, setSituationalImages,
  getLorebookEntries, setLorebookEntries,
  getCreatorDb
} from '../state.js';
import { renderIntroPreview, updateIntroTypeButtons } from '../intro-blocks.js';
import { renderEditorImages } from './images.js';
import { renderSituationalImageList } from './situational.js';
import { renderLorebookList } from './lorebook.js';
import {
  updatePublishButtonState, updateCharCounts, updateImageGenButtonState,
  updateAllFieldValidationStates, updateTabCheckStatus,
  setVisibilityValue
} from './events.js';
import { parseFirstMessageToBlocks } from './parser.js';
import { closeEditor } from './modal.js';
import { setHashtags, initHashtags } from './hashtags.js';

// ============================================
// 에디터 폼 초기화
// ============================================

export function resetEditorForm() {
  setInitialEditorState(null);

  // 다중 이미지 초기화
  getCurrentEditorData().images = [];
  renderEditorImages();

  // 캐릭터 정보 필드 초기화
  const charFields = [
    'editorCharName', 'editorCharPrompt',
    'editorCharAdditionalPrompt', 'editorCharDescription'
  ];
  charFields.forEach(id => {
    const el = document.getElementById(id);
    if (el) el.value = '';
  });

  // 성별, 경력, 음성 초기화 (빈 상태)
  const genderSelect = document.getElementById('editorCharGender');
  if (genderSelect) genderSelect.value = '';

  const experienceInput = document.getElementById('editorExperience');
  if (experienceInput) experienceInput.value = '';

  const voiceSelect = document.getElementById('editorVoiceId');
  if (voiceSelect) voiceSelect.value = '';

  // 타겟 초기화 (빈 상태)
  const targetSelect = document.getElementById('editorTargetAudience');
  if (targetSelect) targetSelect.value = '';

  // 인트로 블록 초기화
  setIntroBlocks([]);
  setCurrentIntroType('narration');
  const introInput = document.getElementById('introComposerInput');
  if (introInput) {
    introInput.value = '';
    introInput.style.height = 'auto';
  }
  renderIntroPreview();
  updateIntroTypeButtons();

  // 대화 예시 초기화
  setExampleDialogues([]);
  renderExampleDialogueList();

  // 상황별 이미지 초기화
  setSituationalImages([]);
  renderSituationalImageList();

  // 로어북 초기화
  setLorebookEntries([]);
  renderLorebookList();

  // 소개 탭 필드 초기화
  const introFields = ['editorShortIntro', 'editorHashtags', 'editorCreatorComment'];
  introFields.forEach(id => {
    const el = document.getElementById(id);
    if (el) el.value = '';
  });

  // 해시태그 칩 초기화
  initHashtags();

  // 추가 프롬프트 토글 초기화
  const additionalPromptToggle = document.getElementById('editorAdditionalPromptToggle');
  const additionalPromptContent = document.getElementById('additionalPromptContent');
  if (additionalPromptToggle) additionalPromptToggle.checked = false;
  if (additionalPromptContent) additionalPromptContent.style.display = 'none';

  // 카운터 및 검증 상태 초기화
  updateCharCounts();
  updateAllFieldValidationStates();
  updateTabCheckStatus();

  // 설정 초기화
  setVisibilityValue('public');
  const safetyFilter = document.getElementById('editorSafetyFilter');
  const showThoughts = document.getElementById('editorShowThoughts');

  if (safetyFilter) safetyFilter.checked = true;
  if (showThoughts) showThoughts.checked = false;

  updatePublishButtonState();
}

// ============================================
// 제작물 로드 (편집용)
// ============================================

export async function loadCreationForEdit(creationId) {
  const user = getCurrentUser();
  if (!user) return;

  const db = getCreatorDb();

  try {
    showGlobalLoading();

    const docRef = db.collection('published').doc(creationId);
    const [doc, detailDoc] = await Promise.all([
      docRef.get(),
      docRef.collection('content').doc('detailData').get()
    ]);

    if (!doc.exists) {
      showToast('제작물을 찾을 수 없습니다');
      closeEditor();
      return;
    }

    const metaData = doc.data();
    if (!detailDoc.exists) {
      showToast('제작물 상세 정보를 찾을 수 없습니다');
      closeEditor();
      return;
    }
    const detailData = detailDoc.data();
    const data = { ...metaData, ...detailData };

    const isOwner = data.creatorId === user.uid || data.authorId === user.uid;
    if (!isOwner && data.status !== 'public') {
      showToast('접근 권한이 없습니다');
      closeEditor();
      return;
    }

    const creation = {
      id: doc.id,
      ...data,
      createdAt: metaData.createdAt?.toDate?.() || null,
      updatedAt: metaData.updatedAt?.toDate?.() || null,
      publishedAt: metaData.publishedAt?.toDate?.() || null
    };

    populateEditorForm(creation);
  } catch (error) {
    console.error('[form.js] 제작물 로드 오류:', error);
    showToast('제작물을 불러오는데 실패했습니다');
    closeEditor();
  } finally {
    hideGlobalLoading();
  }
}

function populateEditorForm(creation) {
  setEditorDataStatus(creation.status || 'draft');
  updateAutoFillFabVisibility();

  // 다중 이미지 로드
  const editorData = getCurrentEditorData();
  editorData.images = [];
  if (creation.images && Array.isArray(creation.images) && creation.images.length > 0) {
    creation.images.forEach(imgUrl => {
      editorData.images.push({ url: imgUrl, file: null });
    });
  } else if (creation.imageUrl) {
    editorData.images.push({ url: creation.imageUrl, file: null });
  }
  renderEditorImages();

  // 캐릭터 정보
  setValue('editorCharName', creation.character?.name || creation.characterName || creation.title || '');
  setValue('editorCharGender', creation.character?.gender || '여성');
  // 하위호환: age → experience
  setValue('editorExperience', creation.character?.experience || creation.character?.age || '');
  setValue('editorVoiceId', creation.character?.voiceId || '');
  setValue('editorCharPrompt', creation.character?.prompt || '');
  setValue('editorCharAdditionalPrompt', creation.character?.additionalPrompt || '');

  // 추가 프롬프트 토글 상태
  const hasAdditionalPrompt = (creation.character?.additionalPrompt || '').trim().length > 0;
  const additionalPromptToggle = document.getElementById('editorAdditionalPromptToggle');
  const additionalPromptContent = document.getElementById('additionalPromptContent');
  if (additionalPromptToggle) additionalPromptToggle.checked = hasAdditionalPrompt;
  if (additionalPromptContent) additionalPromptContent.style.display = hasAdditionalPrompt ? 'block' : 'none';

  // 소개
  setValue('editorCharDescription', creation.characterDescription || creation.description || '');
  setValue('editorTargetAudience', creation.targetAudience || 'all');
  setValue('editorShortIntro', creation.shortIntro || creation.shortDesc || '');
  setValue('editorCreatorComment', creation.creatorComment || '');

  // 태그 로드 (칩 UI)
  const loadedTags = creation.tags || creation.hashtags || [];
  setHashtags(loadedTags);

  // 인트로 블록 파싱
  if (creation.firstMessage) {
    setIntroBlocks(parseFirstMessageToBlocks(creation.firstMessage, creation.introImages || []));
  } else if (creation.introBlocks && Array.isArray(creation.introBlocks)) {
    setIntroBlocks(creation.introBlocks);
  } else {
    setIntroBlocks([]);
  }

  // introImages 복원
  if (creation.introImages && Array.isArray(creation.introImages) && creation.introImages.length > 0) {
    const blocks = getIntroBlocks();
    const hasImageBlock = blocks.some(b => b.type === 'image');
    if (!hasImageBlock) {
      creation.introImages.forEach((url) => {
        blocks.unshift({ type: 'image', content: url });
      });
    }
  }

  renderIntroPreview();

  // 대화 예시 로드
  if (creation.exampleDialogues && Array.isArray(creation.exampleDialogues)) {
    setExampleDialogues(creation.exampleDialogues);
  } else {
    setExampleDialogues([]);
  }
  renderExampleDialogueList();

  // 상황별 이미지 로드
  if (creation.situationalImages && Array.isArray(creation.situationalImages)) {
    setSituationalImages(creation.situationalImages);
  } else {
    setSituationalImages([]);
  }
  renderSituationalImageList();

  // 로어북 로드
  if (creation.lorebook && Array.isArray(creation.lorebook)) {
    setLorebookEntries(creation.lorebook);
  } else {
    setLorebookEntries([]);
  }
  renderLorebookList();

  // 공개 설정 (새 형식)
  const visibility = creation.status || 'public';
  setVisibilityValue(visibility);

  // 기타 설정
  setChecked('editorSafetyFilter', creation.safetyFilter === true);
  setChecked('editorShowThoughts', creation.showThoughts === true);

  updateCharCounts();
  updateAllFieldValidationStates();
  updateTabCheckStatus();
  updatePublishButtonState();

  if (creation.status === 'public' || creation.status === 'link') {
    setInitialEditorState(getCurrentEditorState());
  }
}

// ============================================
// 에디터 상태 관리
// ============================================

export function getCurrentEditorState() {
  const visibilityRadio = document.querySelector('input[name="editorVisibility"]:checked');

  return {
    charName: document.getElementById('editorCharName')?.value || '',
    charGender: document.getElementById('editorCharGender')?.value || '',
    experience: document.getElementById('editorExperience')?.value || '',
    voiceId: document.getElementById('editorVoiceId')?.value || '',
    charPrompt: document.getElementById('editorCharPrompt')?.value || '',
    charAdditionalPrompt: document.getElementById('editorCharAdditionalPrompt')?.value || '',
    charDescription: document.getElementById('editorCharDescription')?.value || '',
    targetAudience: document.getElementById('editorTargetAudience')?.value || 'all',
    shortIntro: document.getElementById('editorShortIntro')?.value || '',
    hashtags: document.getElementById('editorHashtags')?.value || '',
    creatorComment: document.getElementById('editorCreatorComment')?.value || '',
    visibility: visibilityRadio?.value || 'public',
    safetyFilter: document.getElementById('editorSafetyFilter')?.checked || false,
    showThoughts: document.getElementById('editorShowThoughts')?.checked || false,
    introBlocks: JSON.stringify(getIntroBlocks()),
    exampleDialogues: JSON.stringify(getExampleDialogues()),
    situationalImages: JSON.stringify(getSituationalImages()),
    lorebookEntries: JSON.stringify(getLorebookEntries()),
    images: JSON.stringify(getCurrentEditorData().images.map(img => img.url))
  };
}

export function hasEditorStateChanged() {
  if (!getInitialEditorState()) return true;
  const currentState = getCurrentEditorState();
  return JSON.stringify(currentState) !== JSON.stringify(getInitialEditorState());
}

// ============================================
// 대화 예시 리스트 렌더링
// ============================================

export function renderExampleDialogueList() {
  const listEl = document.getElementById('exampleDialogueList');
  if (!listEl) return;

  const dialogues = getExampleDialogues();

  if (dialogues.length === 0) {
    listEl.innerHTML = '';
    return;
  }

  listEl.innerHTML = dialogues.map((dialogue, index) => {
    return `
      <div class="example-dialogue-item" data-index="${index}">
        <span class="example-dialogue-item-title">대화 예시 ${index + 1}</span>
        <div class="example-dialogue-item-actions">
          <button class="example-delete-btn" data-action="delete-example" data-index="${index}">삭제</button>
        </div>
      </div>
    `;
  }).join('');
}

// ============================================
// 자동 채우기 FAB 표시/숨김
// ============================================

export function updateAutoFillFabVisibility() {
  const btn = document.querySelector('.editor-autofill-btn');
  if (!btn) return;

  const editorData = getCurrentEditorData();
  const showBtn = !editorData.id || editorData.status === 'draft';
  btn.style.display = showBtn ? 'flex' : 'none';
}

// ============================================
// 유틸리티
// ============================================

function setValue(id, value) {
  const el = document.getElementById(id);
  if (el) el.value = value;
}

function setChecked(id, checked) {
  const el = document.getElementById(id);
  if (el) el.checked = checked;
}
